import { Suspense, useEffect, useRef, useState, type ReactNode } from 'react';
import { Outlet } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useStore } from '@/store/useStore';
import { reportError } from '@/services/errorTracking';
import { OfflineStatus } from '@/components/OfflineStatus';
import { CommandPalette } from '@/redesign/components/CommandPalette';
import { useAuth } from '@/contexts/AuthContext';
import { captureSampleImportOutcome, sampleImportConsentToken } from '@/services/posthogAnalytics';
import {
  hasAnonymousSession,
  hasSelectedAnonymousCards,
  migrateAnonymousDetailed,
  type MigrationResult,
  type MigrationStatus,
} from '@/utils/anonymousSession';
import { randomUUID } from '@/utils/uuid';
import {
  acknowledgeLocalTryDraft,
  claimPendingTryDraft,
  finishPendingTryDraft,
  isPastRecoveryWindow,
  releasePendingTryDraft,
  withLeaseRenewal,
} from '@/utils/pendingTryDraft';
import { getCachedUserId } from '@/services/userIdCache';

/**
 * Import measurement ids, keyed by the actual shared migration run (the in-flight Promise that
 * migrateAnonymousDetailed hands every caller). StrictMode, a remount or a second loader that
 * shares one run replays that run's ids and each stage is sent once; a later run (a remount that
 * really retries after a final result, or a new draft) is a new Promise and so a new attempt.
 * A WeakMap so finished runs are not kept for the page lifetime.
 */
type RunStage = 'attempt' | 'result';
type ImportRun = { ids: Record<RunStage, string>; sent: Set<RunStage> };
const importRuns = new WeakMap<Promise<MigrationResult>, ImportRun>();
const RESULT_STAGE: Record<
  MigrationStatus,
  'complete' | 'no_write' | 'partial' | 'failed' | 'interrupted'
> = {
  saved: 'complete',
  already_saved: 'no_write',
  nothing_selected: 'no_write',
  no_session: 'no_write',
  partial: 'partial',
  failed: 'failed',
  interrupted: 'interrupted',
};

/** Best effort and once per stage of one run: analytics can never block or fail the import. */
function reportRunStage(
  run: Promise<MigrationResult>,
  userId: string,
  stage: RunStage,
  outcome: 'attempt' | (typeof RESULT_STAGE)[MigrationStatus],
  token: number | undefined,
): void {
  try {
    let entry = importRuns.get(run);
    if (!entry) {
      entry = { ids: { attempt: randomUUID(), result: randomUUID() }, sent: new Set() };
      importRuns.set(run, entry);
    }
    if (entry.sent.has(stage)) return;
    entry.sent.add(stage);
    captureSampleImportOutcome(userId, outcome, entry.ids[stage], token);
  } catch {
    /* measurement only */
  }
}
/** A real retry/skip click is its own unique action, bound to the current account and consent. */
function reportClick(userId: string, outcome: 'retry' | 'skipped'): void {
  try {
    captureSampleImportOutcome(userId, outcome, randomUUID(), safeToken());
  } catch {
    /* measurement only */
  }
}
const safeToken = (): number | undefined => {
  try {
    return sampleImportConsentToken();
  } catch {
    return undefined;
  }
};

const stillSignedInAs = (userId: string): Promise<boolean> =>
  getCachedUserId().then(
    (id) => id === userId,
    () => false,
  );

/**
 * What one recovery attempt found. Only `none` and `durable` let the app load straight away:
 * `durable` means every card is saved (the server copy may or may not be gone yet, see
 * runRecovery). `retry` is anything not proven: a busy or failed claim, a partial import.
 * `measured` says a real draft was claimed and imported, so retry/skip clicks are measurable.
 */
type RecoveryOutcome =
  | { kind: 'none' }
  | { kind: 'durable' }
  | { kind: 'interrupted' }
  | { kind: 'retry'; measured: boolean };

/**
 * One recovery per account at a time, shared by every caller (StrictMode's second effect, a
 * remount, a second loader). The work is bound to the ACCOUNT, never to a component: a
 * component unmounting only stops its own screen updates, while the shared run still imports,
 * acknowledges or releases. Without this, the first effect's lease makes the second effect's
 * claim look busy and the import is silently dropped.
 */
const recoveryFlights = new Map<string, Promise<RecoveryOutcome>>();
function recoverPendingDraft(userId: string): Promise<RecoveryOutcome> {
  const running = recoveryFlights.get(userId);
  if (running) return running;
  const flight = runRecovery(userId).finally(() => recoveryFlights.delete(userId));
  recoveryFlights.set(userId, flight);
  return flight;
}

/**
 * Imports the draft parked by signup, for this account only. The server copy is deleted only
 * when the database confirms it after every card is durable. A failed or refused acknowledgement
 * does not hold Home hostage (the cards are already saved); the lease is released so the next
 * load re-imports idempotently by import key and tries the acknowledgement again.
 * Known gap: if that acknowledgement keeps failing AND the learner deletes an imported card
 * before it succeeds, the next load can re-import it.
 */
async function runRecovery(userId: string): Promise<RecoveryOutcome> {
  const claim = await claimPendingTryDraft(userId);
  if (claim.status === 'none') return { kind: 'none' };
  // busy or error is not "nothing waiting": the learner is offered a retry. Nothing was claimed,
  // so there is nothing to import, acknowledge or release.
  if (claim.status !== 'claimed') return { kind: 'retry', measured: false };
  const { lease } = claim;
  // The claim awaited the network: a switched account must not import or acknowledge it.
  if (!(await stillSignedInAs(userId))) {
    await releasePendingTryDraft(userId, lease);
    return { kind: 'interrupted' };
  }
  const token = safeToken();
  let run: Promise<MigrationResult> | null = null;
  const result = await withLeaseRenewal(userId, lease, () => {
    run = migrateAnonymousDetailed(userId, { recovered: claim.draft });
    reportRunStage(run, userId, 'attempt', 'attempt', token);
    return run;
  }).catch(() => null);
  const accountStillSame = await stillSignedInAs(userId);
  // A result that lands after an account switch belongs to a stale attempt: drop it.
  if (run && accountStillSame) {
    reportRunStage(run, userId, 'result', result ? RESULT_STAGE[result.status] : 'failed', token);
  }
  const durable = result?.status === 'saved' || result?.status === 'already_saved';
  if (!durable || !accountStillSame) {
    await releasePendingTryDraft(userId, lease);
    return result?.status === 'interrupted' || !accountStillSame
      ? { kind: 'interrupted' }
      : { kind: 'retry', measured: true };
  }
  if (!(await finishPendingTryDraft(userId, lease))) await releasePendingTryDraft(userId, lease);
  return { kind: 'durable' };
}

/** Each local run acknowledges its matching waiting server copy once, whoever awaits it. */
const acknowledgedLocalRuns = new WeakSet<Promise<MigrationResult>>();

/**
 * Loads the signed-in user's data once, then renders the app.
 *
 * Cards made in /try are saved to the account HERE, before the first
 * loadFromApi. That is the one loading boundary the sign-in race needed: the
 * Supabase auth callback cannot await auth calls, and a migration started
 * there raced this load, which then read default settings and sent a new
 * account to onboarding. Running first means loadFromApi sees the saved
 * settings and cards. The run shares its flight with the Try wall's button.
 * If the cards are not saved, the visitor sees why and chooses: try again, or
 * skip for now (the cards stay on this device and the next load retries).
 */
export function DataLoader({ children }: { children?: ReactNode }) {
  const { t: tCommon } = useTranslation('common');
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const dataAlreadyLoaded = useStore((s) => s.dataLoaded);
  const [loading, setLoading] = useState(!dataAlreadyLoaded || hasAnonymousSession());
  const [loadError, setLoadError] = useState<string | null>(null);
  // Bound to the account that produced it, so one account's retry screen or skip never applies
  // to the next account on the same page.
  const [tryCardsPending, setTryCardsPending] = useState<{
    userId: string;
    source: 'local' | 'recovered';
    measured: boolean;
  } | null>(null);
  const skipTryCardsFor = useRef<string | null>(null);
  const createdAt = user?.created_at;
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    let cancelled = false;

    async function init() {
      // Read before migration: only a store loaded by someone else skips follow-ups.
      const loadedBefore = useStore.getState().dataLoaded;
      // A store that is already loaded still gets its Try cards saved (the
      // visitor can arrive here from /try), but is not reloaded from scratch.
      if (userId && skipTryCardsFor.current !== userId) {
        let saved = false;
        // Only a selected draft that actually exists is an import; no draft, or only demo
        // samples, is not an attempt (the migration still cleans legacy demos up).
        const hasDraft = hasAnonymousSession();
        const selected = hasDraft && hasSelectedAnonymousCards();
        const token = safeToken();
        let finalOutcome: (typeof RESULT_STAGE)[MigrationStatus] = 'failed';
        let run: Promise<MigrationResult> | null = null;
        let pendingSource: 'local' | 'recovered' = 'local';
        let pendingMeasured = selected;
        try {
          if (hasDraft) {
            run = migrateAnonymousDetailed(userId);
            if (selected) reportRunStage(run, userId, 'attempt', 'attempt', token);
            const result = await run;
            saved = result.complete;
            finalOutcome = RESULT_STAGE[result.status];
            // Proven durable and the local copy is forgotten: delete the waiting server copy of
            // THIS session only (an unrelated waiting copy is untouched and recovered later), so
            // it cannot resurrect a card the learner deletes. Never blocks or fails the load.
            if (
              (result.status === 'saved' || result.status === 'already_saved') &&
              result.sourceSessionId &&
              !acknowledgedLocalRuns.has(run)
            ) {
              acknowledgedLocalRuns.add(run);
              // The run awaited the network: never acknowledge for an account that is no longer
              // the actor (the database also refuses a JWT that is not userId).
              if (await stillSignedInAs(userId)) {
                await acknowledgeLocalTryDraft(userId, result.sourceSessionId).catch(() => false);
              }
            }
          } else if (isPastRecoveryWindow(createdAt)) {
            // The Auth account is older than any servable draft: skip the round trip. A hint
            // only, never permission; a missing or odd date falls through to the claim.
            saved = true;
          } else {
            // No local draft: the learner may have confirmed in another browser. A local draft
            // always wins this load (its matching server copy is acknowledged above; any other
            // waiting copy is recovered by the next load with no local draft).
            const outcome = await recoverPendingDraft(userId);
            saved = outcome.kind !== 'retry';
            if (outcome.kind === 'retry') {
              pendingSource = 'recovered';
              pendingMeasured = outcome.measured;
            }
          }
        } catch (err) {
          reportError(err, { operation: 'dataLoader.migrateTryCards' });
        }
        // A late result after an account switch belongs to a stale attempt: drop it.
        if (selected && run && !cancelled)
          reportRunStage(run, userId, 'result', finalOutcome, token);
        if (!saved) {
          if (!cancelled) {
            setTryCardsPending({ userId, source: pendingSource, measured: pendingMeasured });
            setLoading(false);
          }
          return;
        }
      }
      if (cancelled) return;
      if (loadedBefore) {
        setLoading(false);
        return;
      }

      const { loadFromApi, loadFullReviewHistory, syncOfflineReviews } = useStore.getState();

      // A successful migration already refreshed the main data (refreshFromApi
      // runs loadFromApi), so skip the duplicate load but still run the
      // follow-ups below, which nothing else would start.
      if (useStore.getState().dataLoaded) {
        setLoading(false);
      } else {
        try {
          await loadFromApi();
        } catch (err) {
          reportError(err, { operation: 'dataLoader.loadFromApi' });
          if (!cancelled) {
            setLoadError(err instanceof Error ? err.message : 'Failed to load data');
          }
        } finally {
          if (!cancelled) setLoading(false);
        }
      }
      if (cancelled) return;

      // Fetch full review history (loadFromApi only fetches 30 days).
      // Both of these are fire-and-forget — errors are reported inside the
      // store actions themselves and shouldn't block the router.
      void loadFullReviewHistory();

      // Sync any reviews queued while offline
      void syncOfflineReviews();

      // Load tutor data (concepts, curriculum, sessions) — fire-and-forget
      void useStore.getState().loadTutorData();
    }

    void init();

    // Listen for network restoration to sync queued reviews
    const handleOnline = () => {
      void useStore.getState().syncOfflineReviews();
    };
    window.addEventListener('online', handleOnline);
    return () => {
      cancelled = true;
      window.removeEventListener('online', handleOnline);
    };
  }, [retryCount, userId, createdAt]);

  if (tryCardsPending && tryCardsPending.userId === userId) {
    const recovered = tryCardsPending.source === 'recovered';
    return (
      <div
        className="flex flex-col items-center justify-center min-h-dvh gap-4 p-6"
        style={{ backgroundColor: 'var(--bg)', color: 'var(--text-primary)' }}
      >
        {recovered ? (
          <>
            <h1 className="text-lg font-medium">Saving your photo choices</h1>
            <p role="alert">
              We couldn't finish saving them yet. Anything you chose is kept for up to 7 days, so
              you can try again now or later.
            </p>
          </>
        ) : (
          <>
            <h1 className="text-lg font-medium">Your photo choices are safe</h1>
            <p role="alert">
              We couldn't save your sample cards yet. They're still on this device.
            </p>
          </>
        )}
        <button
          type="button"
          className="px-4 py-2 rounded-lg font-medium"
          style={{ backgroundColor: 'var(--action)', color: 'var(--on-action)' }}
          onClick={() => {
            // A real retry is its own measured stage and starts a new attempt.
            if (userId && (!recovered || tryCardsPending.measured)) reportClick(userId, 'retry');
            setTryCardsPending(null);
            setLoading(true);
            setRetryCount((c) => c + 1);
          }}
        >
          Try again
        </button>
        <button
          type="button"
          className="px-4 py-2"
          onClick={() => {
            // A known explicit skip: choices may remain, so it is reported as a skip, never complete.
            const known = recovered ? tryCardsPending.measured : hasSelectedAnonymousCards();
            if (userId && known) reportClick(userId, 'skipped');
            skipTryCardsFor.current = userId;
            setTryCardsPending(null);
            setLoading(true);
            setRetryCount((c) => c + 1);
          }}
        >
          Continue without these samples
        </button>
      </div>
    );
  }

  if (loadError) {
    return (
      <div
        className="flex flex-col items-center justify-center min-h-dvh gap-4 p-6"
        style={{ backgroundColor: 'var(--bg)', color: 'var(--text-primary)' }}
      >
        <p className="text-lg font-medium" style={{ color: 'var(--action)' }}>
          Something went wrong
        </p>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
          {loadError}
        </p>
        <button
          onClick={() => {
            setLoadError(null);
            setLoading(true);
            setRetryCount((c) => c + 1);
          }}
          className="px-4 py-2 rounded-lg font-medium"
          style={{ backgroundColor: 'var(--action, #bc4929)', color: '#fff' }}
        >
          Try again
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="flex items-center justify-center min-h-dvh"
        style={{ backgroundColor: 'var(--bg)' }}
      >
        <div
          className="w-8 h-8 border-3 border-t-transparent rounded-full animate-spin"
          style={{ borderColor: 'var(--action, #bc4929)', borderTopColor: 'transparent' }}
        />
      </div>
    );
  }

  return (
    <>
      {/* App-level offline/sync status. Mounted here (not Layout) so it also
          covers the full-screen /review route, which lives outside Layout.
          Kept outside Suspense so it stays mounted across page transitions. */}
      <OfflineStatus />
      {/* Global ⌘K palette (bd Vidi-w9i). Mounted here — like OfflineStatus
          — so it covers every signed-in route including the full-screen
          /review flow, where it stays a NO-OP while the session owns the
          keyboard (reviewKeymapSignal). `t` comes as a prop per the
          redesign manualChunks law (AppChrome.tsx header comment). */}
      <CommandPalette t={tCommon} />
      <Suspense
        fallback={
          <div
            className="flex items-center justify-center min-h-[60vh]"
            style={{ backgroundColor: 'var(--bg)' }}
          >
            <div
              className="w-8 h-8 border-3 border-t-transparent rounded-full animate-spin"
              style={{ borderColor: 'var(--action, #bc4929)', borderTopColor: 'transparent' }}
            />
          </div>
        }
      >
        {/* Additive optional children (the Layout pattern): routes moved out
            of the protected group by the meta port (bd Vidi-hee) wrap their
            signed-in branch in DataLoader directly; the Outlet default keeps
            every nested route byte-identical. */}
        {children ?? <Outlet />}
      </Suspense>
    </>
  );
}
